import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Gift, Search, Plus, Minus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminRewards = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  const load = () => {
    setLoading(true);
    axios.get(`${API}/admin/customers`, { withCredentials: true })
      .then(r => setCustomers(r.data || []))
      .catch(() => toast.error('Could not load customers'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const adjust = async (c, delta) => {
    const input = window.prompt(`Adjust points for ${c.email}. Enter a number (use a negative to deduct):`, delta > 0 ? '100' : '-100');
    if (input === null) return;
    const pts = parseInt(input, 10);
    if (Number.isNaN(pts) || pts === 0) { toast.error('Enter a non-zero number'); return; }
    try {
      const r = await axios.post(`${API}/admin/customers/${c.user_id}/points`, { points: pts, reason: 'Admin adjustment' }, { withCredentials: true });
      toast.success(`Updated — new balance ${r.data.points_balance}`);
      setCustomers(prev => prev.map(x => x.user_id === c.user_id ? { ...x, points_balance: r.data.points_balance } : x));
    } catch { toast.error('Adjustment failed'); }
  };

  const filtered = customers.filter(c =>
    !q || (c.email || '').toLowerCase().includes(q.toLowerCase()) || (c.name || '').toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="bg-white rounded-2xl border border-border p-6" data-testid="admin-rewards-panel">
      <div className="flex items-center gap-2 mb-1"><Gift className="w-5 h-5 text-emerald-700" /><h3 className="text-lg font-bold">IIY Rewards — Customers & Points</h3></div>
      <p className="text-sm text-muted-foreground mb-4">Earn 2 points per £1 (ex-VAT) · 500 points = £5 off · 100 welcome points on sign-up. Adjust balances manually below.</p>
      <div className="relative mb-4 max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name or email" className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-border" data-testid="rewards-search" />
      </div>
      {loading ? <p className="text-sm text-muted-foreground">Loading…</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="rewards-table">
            <thead><tr className="text-left text-muted-foreground border-b border-border">
              <th className="py-2 pr-4">Customer</th><th className="py-2 pr-4">Points</th><th className="py-2 pr-4">Reward</th><th className="py-2">Adjust</th>
            </tr></thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.user_id} className="border-b border-border/60" data-testid={`rewards-row-${c.user_id}`}>
                  <td className="py-2.5 pr-4"><div className="font-medium">{c.name || '—'}</div><div className="text-xs text-muted-foreground">{c.email}</div></td>
                  <td className="py-2.5 pr-4 font-bold" data-testid={`rewards-balance-${c.user_id}`}>{Number(c.points_balance || 0).toLocaleString()}</td>
                  <td className="py-2.5 pr-4">{Number(c.points_balance || 0) >= 500 ? <span className="text-emerald-700 font-medium">£5 ready</span> : <span className="text-muted-foreground">{500 - Number(c.points_balance || 0)} to go</span>}</td>
                  <td className="py-2.5">
                    <div className="flex gap-1.5">
                      <button onClick={() => adjust(c, 1)} className="px-2 py-1 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700" data-testid={`rewards-add-${c.user_id}`}><Plus className="w-3.5 h-3.5" /></button>
                      <button onClick={() => adjust(c, -1)} className="px-2 py-1 rounded-lg bg-rose-50 border border-rose-200 text-rose-700" data-testid={`rewards-sub-${c.user_id}`}><Minus className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-muted-foreground">No customers found.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
